import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { FiEdit2, FiPlus, FiSlash, FiCheckCircle, FiEye, FiPaperclip, FiLayout } from 'react-icons/fi';
import { useDepartments } from '../hooks/useDepartments';
import { useDepartmentActions } from '../hooks/useDepartmentActions';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import StatusPill from '@core/components/StatusPill';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import {
  NAME_SORT_FILTER_FIELD,
  SORT_BY_DEPARTMENT_NAME,
  SORT_DIR_ASC,
  countContentFilters,
  normalizeSortDir,
  readSortDirFromSearchParams,
  withCatalogSort,
} from '@core/constants/listSort';
import { useDebounce } from '@core/hooks/useDebounce';
import { useSearchUrlSync } from '@core/hooks/useSearchUrlSync';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '@core/components/Pagination';
import { Department } from '../types/department';
import CreateDepartmentModal from '@features/department/components/CreateDepartmentModal';
import UpdateDepartmentModal from '@features/department/components/UpdateDepartmentModal';
import DepartmentReportDocumentsModal from '@features/department/components/DepartmentReportDocumentsModal';
import StatusChangeConfirmModal, { StatusChangeKind } from '@features/admin/finance/components/StatusChangeConfirmModal';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES, DOCUMENT_CATEGORY_MANAGE_CODES, PRESENTATION_LAYOUT_MANAGE_CODES } from '@features/entity/types/actionCodes';
import { buildDefaultListParams } from '@core/utils/listQuery';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { ErrorResponse } from '@core/types/errorResponse';
import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';

const DEPARTMENT_ACTION_CODES = [
  ACTION_CODES.CREATE_DEPARTMENT,
  ACTION_CODES.ENABLE_DEPARTMENT,
  ACTION_CODES.DISABLE_DEPARTMENT,
  ACTION_CODES.UPDATE_DEPARTMENT,
  ...DOCUMENT_CATEGORY_MANAGE_CODES,
  ...PRESENTATION_LAYOUT_MANAGE_CODES,
];

const DEPARTMENT_FILTER_FIELDS: FilterField[] = [
  NAME_SORT_FILTER_FIELD,
  {
    name: 'isActive',
    label: 'Status',
    type: 'select',
    options: [
      { value: '1', label: 'Active' },
      { value: '0', label: 'Inactive' },
    ],
  },
];

const AllDepartmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const urlNationId = useParams<{ nationId?: string }>().nationId;

  const nationId = useMemo(() => {
    if (urlNationId) {
      const n = Number(urlNationId);
      if (Number.isFinite(n) && n > 0) return n;
    }
    try {
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_ENTITY_CONTEXT);
      if (cached) {
        const ctx = JSON.parse(cached);
        if (ctx.scope === 'NATION') return ctx.scopeId as number;
      }
    } catch { /* ignore */ }
    return null;
  }, [urlNationId]);

  const [urlParams] = useSearchParams();

  const {
    departments,
    loading,
    error,
    pagination,
    refetch,
    updateParams,
    validationErrors
  } = useDepartments(
    withCatalogSort(
      {
        ...buildDefaultListParams(
          {
            perPage: 20,
            page: 1,
            ...(nationId ? { nationId } : {}),
            ...(urlParams.get('q') ? { departmentName: urlParams.get('q')! } : {}),
          },
          ['departmentName']
        ),
        sortDir: readSortDirFromSearchParams(urlParams),
      },
      SORT_BY_DEPARTMENT_NAME
    )
  );

  const [showCreateDepartmentModal, setShowCreateDepartmentModal] = useState(false);
  const [showUpdateDepartmentModal, setShowUpdateDepartmentModal] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [reportMediaDepartment, setReportMediaDepartment] = useState<{ id: number; name: string } | null>(null);
  const [query, setQuery] = useState(() => urlParams.get('q') ?? '');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(() => ({
    sortDir: readSortDirFromSearchParams(urlParams),
    ...(urlParams.get('isActive') ? { isActive: urlParams.get('isActive')! } : {}),
  }));
  const [statusConfirm, setStatusConfirm] = useState<{ department: Department; kind: StatusChangeKind } | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  const showError = useApiError();
  const { busy: statusBusy, doDepartmentDisable, doDepartmentEnable } = useDepartmentActions();

  // ADMIN-only enable/disable is gated on NATION-scoped action codes.
  const { hasPermission } = usePermissions({
    scopeType: 'NATION',
    scopeId: nationId ?? undefined,
    actionCodes: DEPARTMENT_ACTION_CODES,
  });
  const canCreate = hasPermission(ACTION_CODES.CREATE_DEPARTMENT);
  const canDisable = hasPermission(ACTION_CODES.DISABLE_DEPARTMENT);
  const canEnable = hasPermission(ACTION_CODES.ENABLE_DEPARTMENT);
  const canEdit = hasPermission(ACTION_CODES.UPDATE_DEPARTMENT);
  const canManageReportMedia = DOCUMENT_CATEGORY_MANAGE_CODES.some((code) => hasPermission(code));
  const canManageLayouts = PRESENTATION_LAYOUT_MANAGE_CODES.some((code) => hasPermission(code));

  useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page: pagination?.current_page ?? 1 });

  useEffect(() => {
    updateParams(
      withCatalogSort(
        {
          ...buildDefaultListParams({ departmentName: debouncedQuery || undefined }, ['departmentName']),
          sortDir: normalizeSortDir(activeFilters.sortDir),
        },
        SORT_BY_DEPARTMENT_NAME
      )
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handlePageChange = (page: number) => {
    updateParams({ page });
  };

  const handleFiltersApply = (filters: Record<string, any>) => {
    const next: Record<string, string> = {
      ...Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)])
      ),
      sortDir: normalizeSortDir(filters.sortDir),
    };
    setActiveFilters(next);
    const isActive = next.isActive === '1' ? true : next.isActive === '0' ? false : undefined;
    updateParams(
      withCatalogSort(
        {
          isActive,
          page: 1,
          sortDir: next.sortDir,
        },
        SORT_BY_DEPARTMENT_NAME
      )
    );
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirm || !nationId) return;
    const { department, kind } = statusConfirm;
    const response = kind === 'disable'
      ? await doDepartmentDisable(nationId, department.department_id)
      : await doDepartmentEnable(nationId, department.department_id);

    if (response.isSuccess()) {
      showSuccess(response.getData()?.message ?? `Department ${kind}d successfully.`);
      setStatusConfirm(null);
      refetch();
    } else {
      showError(response.getError() as ErrorResponse);
    }
  };

  // Table columns configuration
  const columns: DataTableColumn<Department>[] = [
    {
      key: 'name',
      label: 'Department Name',
      role: 'title',
      render: (value: string, _row: Department) => (
        <div>
          <div className="fw-medium">{value}</div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      role: 'description',
      render: (value: string) => (
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {value || 'No description provided'}
        </div>
      ),
      // On the card the description wraps fully (no truncation) and is omitted
      // when empty.
      cardRender: (value: string) => value || null,
    },
    {
      key: 'is_active',
      label: 'Status',
      role: 'status',
      render: (_value: boolean, row: Department) => (
        <StatusPill active={row.is_active} />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      role: 'actions',
      width: '1%',
      className: 'text-end',
      render: (value: any, row: Department) => (
        <TableActions aria-label={`Actions for department ${row.name}`}>
          <TableActionButton
            icon={canEdit ? <FiEdit2 aria-hidden="true" /> : <FiEye aria-hidden="true" />}
            label={canEdit ? 'Edit' : 'View'}
            primary
            variant="outline-secondary"
            onClick={() => { setShowUpdateDepartmentModal(true); setSelectedDepartmentId(row.department_id); }}
            data-testid={`department-actions-edit-button-${row.department_id}`}
          />
          <TableActionButton
            icon="journal-text"
            label="Templates"
            primary
            onClick={() => {
              if (nationId) {
                navigate(`/nation/${nationId}/department/templates?did=${row.department_id}`);
              } else {
                navigate('/entities');
              }
            }}
            data-testid={`department-actions-reports-button-${row.department_id}`}
          />
          <TableActionButton
            icon={<FiPaperclip aria-hidden="true" />}
            label={canManageReportMedia ? "Manage report documents" : "View report documents"}
            onClick={() => {
              if (nationId) {
                setReportMediaDepartment({ id: row.department_id, name: row.name });
              } else {
                navigate("/entities");
              }
            }}
            data-testid={`department-actions-report-media-button-${row.department_id}`}
          />
          {canManageLayouts && nationId ? (
            <TableActionButton
              icon={<FiLayout aria-hidden="true" />}
              label="Presentation Layouts"
              onClick={() => navigate(`/nation/${nationId}/department/presentation-layouts?did=${row.department_id}`)}
              data-testid={`department-actions-presentation-layouts-button-${row.department_id}`}
            />
          ) : null}
          {row.is_active && canDisable && (
            <TableActionButton
              icon={<FiSlash />}
              label="Disable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--danger"
              onClick={() => setStatusConfirm({ department: row, kind: 'disable' })}
              data-testid={`department-actions-disable-button-${row.department_id}`}
            />
          )}
          {!row.is_active && canEnable && (
            <TableActionButton
              icon={<FiCheckCircle />}
              label="Enable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--success"
              onClick={() => setStatusConfirm({ department: row, kind: 'enable' })}
              data-testid={`department-actions-enable-button-${row.department_id}`}
            />
          )}
        </TableActions>
      )
    }
  ];

  const showCreateButton = nationId != null && Number.isFinite(nationId) && nationId > 0 && canCreate;

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1" data-testid="departments-page-title">All Departments</h1>
          <p className="text-muted mb-0" data-testid="departments-page-description">
            Manage and view department information
          </p>
        </div>
        {showCreateButton && (
          <Button
            variant="primary"
            onClick={() => setShowCreateDepartmentModal(true)}
            aria-label="Create Department"
            className="shadow-sm"
            data-testid="create-department-button"
          >
            <FiPlus className="me-2" />
            Create Department
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search departments…"
          initialQuery={query}
          filterFields={DEPARTMENT_FILTER_FIELDS}
          initialFilters={activeFilters}
          activeFilterCount={countContentFilters(activeFilters)}
          onQueryChange={setQuery}
          onFiltersApply={handleFiltersApply}
          onClear={() => {
            setQuery('');
            setActiveFilters({ sortDir: SORT_DIR_ASC });
            updateParams(
              withCatalogSort(
                {
                  ...buildDefaultListParams({ departmentName: undefined }, ['departmentName']),
                  isActive: undefined,
                  sortDir: SORT_DIR_ASC,
                },
                SORT_BY_DEPARTMENT_NAME
              )
            );
          }}
          loading={loading}
          testId="departments-search"
        />
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-4" data-testid="departments-error-alert">
          {error}
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="warning" className="mb-4" data-testid="departments-validation-alert">
          <strong>Validation Errors:</strong>
          <ul className="mb-0 mt-2">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Data Table */}
      <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="departments-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={departments}
            columns={columns}
            rowNumberOffset={(pagination?.from ?? 1) - 1}
            loading={loading}
            error={error}
            emptyMessage="No departments found"
            testId="departments-table"
            cardAriaLabel={(row) => `Department ${row.name}`}
            striped={true}
            hover={true}
            responsive={true}
          />
        </Card.Body>
      </Card>
      {/* Pagination */}
      {pagination && (
        <Pagination
          meta={pagination}
          onPageChange={handlePageChange}
          testId="departments-pagination"
          showInfo={true}
          showPerPageSelector={false}
          className="mt-3"
        />
      )}

      <CreateDepartmentModal
        show={showCreateDepartmentModal}
        onHide={() => setShowCreateDepartmentModal(false)}
        nationId={nationId ?? 0}
        onChanged={refetch}
      />

      <UpdateDepartmentModal
        show={showUpdateDepartmentModal}
        onHide={() => setShowUpdateDepartmentModal(false)}
        departmentId={selectedDepartmentId}
        onClearDepartmentId={() => setSelectedDepartmentId(null)}
        onUpdated={refetch}
        readOnly={!canEdit}
      />

      <DepartmentReportDocumentsModal
        show={reportMediaDepartment != null}
        onHide={() => setReportMediaDepartment(null)}
        nationId={nationId ?? 0}
        departmentId={reportMediaDepartment?.id ?? 0}
        departmentName={reportMediaDepartment?.name ?? ""}
      />

      <StatusChangeConfirmModal
        show={!!statusConfirm}
        kind={statusConfirm?.kind ?? 'disable'}
        entityLabel="department"
        entityName={statusConfirm?.department.name ?? ''}
        busy={statusBusy}
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusConfirm(null)}
        bodyText={statusConfirm?.kind === 'disable' ? (
          <>
            Are you sure you want to disable <strong>{statusConfirm?.department.name}</strong>? New report templates can&apos;t be added to it and churches can&apos;t submit reports against it. Existing reports are unaffected.
          </>
        ) : undefined}
      />

    </Container>
  );
};

export default AllDepartmentsPage;